"use client";

import { useState } from "react";
import { Button } from "@excellent/ui";
import { Card, CardContent, CardHeader, CardTitle } from "@excellent/ui";
import { apiCall } from "@excellent/ui/api";
import { help } from "@excellent/ui/api/contracts/help";

/**
 * Danger-zone Re-seed control on /help/settings. Calls the
 * help.seed.apply endpoint with force=true so the seed pipeline
 * deletes existing source='seed' rows (shell-owned AND per-product
 * seed articles) and rewrites them from the current product
 * manifests.
 *
 * User-authored articles (source='user') are NOT touched. Two-click
 * confirm — first click reveals the actual "Re-seed" button.
 */
export function HelpReseedButton() {
  const [armed, setArmed] = useState(false);
  const [pending, setPending] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function reseed() {
    setError(null);
    setPending(true);
    try {
      const r = await apiCall(help.seed.apply, { body: { force: true } });
      const i = r.inserted;
      setResult(
        `Re-seeded: ${i.categories} categories, ${i.articles} shell articles, ${i.product_categories} product categories, ${i.product_articles} product articles, ${i.external_links} links.`,
      );
      setArmed(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Re-seed failed");
    } finally {
      setPending(false);
    }
  }

  return (
    <Card className="border-destructive/40">
      <CardHeader>
        <CardTitle className="type-title">Danger zone</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="type-body text-muted-foreground">
          Re-seeding deletes all seed-owned categories and articles
          (shell and per-product) and rewrites them from the current
          manifests. User-authored articles are preserved.
        </p>
        <div className="flex gap-2">
          {!armed ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setArmed(true)}
            >
              Re-seed help content
            </Button>
          ) : (
            <>
              <Button
                type="button"
                size="sm"
                variant="destructive"
                onClick={reseed}
                disabled={pending}
              >
                {pending ? "Re-seeding…" : "Yes, re-seed"}
              </Button>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => setArmed(false)}
                disabled={pending}
              >
                Cancel
              </Button>
            </>
          )}
        </div>
        {result ? (
          <p className="type-body text-foreground">{result}</p>
        ) : null}
        {error ? <p className="type-body text-destructive">{error}</p> : null}
      </CardContent>
    </Card>
  );
}
